import styled, { keyframes } from "styled-components";
import { mediaMax } from "components/designV2/breakpoints";
import { Radius, Spacing } from "components/designV2/constants";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";

export const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.lg}px;
`;

export const Header = styled.div`
  align-items: flex-end;
  display: flex;
  gap: ${Spacing.md}px;
  justify-content: space-between;

  ${mediaMax.mobile} {
    align-items: stretch;
    flex-direction: column;
  }
`;

export const HeaderActions = styled.div`
  align-items: flex-end;
  display: flex;
  gap: ${Spacing.md}px;

  ${mediaMax.mobile} {
    align-items: stretch;
    flex-direction: column;
  }
`;

export const HeaderCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm}px;
`;

export const Intro = styled.p`
  ${TypographyV2.b2};
  color: ${ColorV2.Blue4};
  margin: 0;
`;

export const UnitList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md}px;
`;

export const UnitCard = styled.section`
  background: ${ColorV2.Blue7};
  border-radius: ${Radius.xl}px;
  padding: ${Spacing.md}px;
`;

export const UnitHeader = styled.div`
  align-items: center;
  display: flex;
  gap: ${Spacing.sm}px;
  justify-content: space-between;
`;

export const UnitTitleWrap = styled.div`
  min-width: 0;
`;

export const UnitTitleRow = styled.div`
  align-items: center;
  display: flex;
  gap: ${Spacing.sm}px;
`;

export const UnitTitle = styled.h3`
  ${TypographyV2.b1};
  color: ${ColorV2.Blue1};
  margin: 0;
`;

export const Meta = styled.p`
  ${TypographyV2.s1};
  color: ${ColorV2.Blue4};
  margin: ${Spacing.xs}px 0 0;
`;

export const IconButton = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: ${Radius.md}px;
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  padding: ${Spacing.sm}px;

  &:not(:disabled):hover {
    background: ${ColorV2.Blue6};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

export const TeamList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm}px;
  margin-top: ${Spacing.md}px;
`;

export const TeamRow = styled.div`
  align-items: center;
  background: ${ColorV2.White};
  border-radius: ${Radius.lg}px;
  display: grid;
  gap: ${Spacing.md}px;
  grid-template-columns: minmax(220px, 280px) minmax(180px, 1fr) 88px;
  min-height: 56px;
  padding: ${Spacing.sm}px ${Spacing.md}px;

  ${mediaMax.mobile} {
    gap: ${Spacing.xs}px ${Spacing.md}px;
    grid-template-columns: minmax(0, 1fr) auto;
  }
`;

export const TeamName = styled.span`
  ${TypographyV2.b2};
  background: ${ColorV2.Blue7};
  border-radius: ${Radius.md}px;
  color: ${ColorV2.Blue2};
  justify-self: start;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  padding: ${Spacing.sm}px ${Spacing.md}px;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: fit-content;

  ${mediaMax.mobile} {
    grid-column: 1;
    grid-row: 1;
  }
`;

export const TeamCount = styled.span`
  ${TypographyV2.s1};
  color: ${ColorV2.Blue2};

  ${mediaMax.mobile} {
    grid-column: 1;
    grid-row: 2;
    padding-left: ${Spacing.md}px;
  }
`;

export const RowActions = styled.div`
  align-items: center;
  display: flex;
  gap: ${Spacing.xs}px;
  justify-content: flex-end;

  ${mediaMax.mobile} {
    grid-column: 2;
    grid-row: 1 / span 2;
  }
`;

export const AddTeamRow = styled.div`
  display: flex;
  justify-content: center;
  padding-top: ${Spacing.sm}px;
`;

export const EmptyState = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm}px;
  justify-content: center;
  min-height: 220px;
  text-align: center;
`;

export const EmptyTitle = styled.h3`
  ${TypographyV2.b1};
  color: ${ColorV2.Blue1};
  margin: 0;
`;

export const EmptyText = styled.p`
  ${TypographyV2.s1};
  color: ${ColorV2.Blue4};
  margin: 0 0 ${Spacing.sm}px;
`;

export const ErrorBox = styled.div`
  ${TypographyV2.b2};
  background: ${ColorV2.NegativeRedLight};
  border-radius: ${Radius.md}px;
  color: ${ColorV2.NegativeRed};
  flex: 1;
  padding: ${Spacing.md}px;
`;

export const ErrorRow = styled.div`
  align-items: center;
  display: flex;
  gap: ${Spacing.md}px;

  ${mediaMax.mobile} {
    align-items: stretch;
    flex-direction: column;
  }
`;

export const DialogForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.lg}px;
`;

export const DialogCopy = styled.p`
  ${TypographyV2.b2};
  color: ${ColorV2.Blue4};
  margin: 0;
`;

export const DialogActions = styled.div`
  display: flex;
  gap: ${Spacing.md}px;
  justify-content: flex-end;
`;

export const PanelSection = styled.section`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md}px;
  width: 100%;
`;

export const PanelActionRow = styled.div`
  align-items: end;
  display: grid;
  column-gap: ${Spacing.lg}px;
  grid-template-columns: minmax(0, 1fr) auto;
  width: 100%;

  > button {
    flex-shrink: 0;
    white-space: nowrap;
  }

  ${mediaMax.mobile} {
    grid-template-columns: minmax(0, 1fr);
    row-gap: ${Spacing.md}px;

    > button {
      justify-self: end;
    }
  }
`;

export const PanelActionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm}px;
  min-width: 0;
`;

export const PanelInputContent = styled(PanelActionContent)`
  max-width: 260px;
  width: 100%;

  ${mediaMax.mobile} {
    max-width: none;
  }
`;

export const PanelSectionTitle = styled.h3`
  ${TypographyV2.h5};
  color: ${ColorV2.Blue1};
  margin: 0;
`;

export const PanelRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm}px;
  width: 100%;
`;

export const MemberRow = styled.div<{ $isInactive?: boolean }>`
  align-items: center;
  background: ${ColorV2.White};
  border-radius: ${Radius.lg}px;
  display: flex;
  gap: ${Spacing.sm}px;
  justify-content: space-between;
  min-height: 64px;
  overflow: hidden;
  padding: ${Spacing.xs}px ${Spacing.sm}px;
  position: relative;

  > div:last-child {
    align-self: center;
    margin-left: auto;
    position: relative;
    z-index: 1;
  }

  > button:last-child {
    position: relative;
    z-index: 1;
  }

  &::after {
    background: ${ColorV2.Blue6};
    content: "";
    inset: 0;
    opacity: ${(props) => (props.$isInactive ? 0.5 : 0)};
    pointer-events: none;
    position: absolute;
  }
`;

export const MemberIdentity = styled.div`
  min-width: 0;
  flex: 1;
`;

export const DangerCopy = styled.p`
  ${TypographyV2.s1};
  color: ${ColorV2.Blue4};
  margin: 0;
`;

export const LoadingText = styled.p`
  ${TypographyV2.b2};
  color: ${ColorV2.Blue4};
  margin: ${Spacing.xl}px 0;
  text-align: center;
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

export const LoadingIndicator = styled.span`
  animation: ${spin} 0.9s linear infinite;
  display: inline-flex;
`;

export const PanelLoading = styled.div`
  align-items: center;
  color: ${ColorV2.Blue1};
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md}px;
  justify-content: center;
  min-height: 240px;
`;
